/**
 * useTableScroll Hook
 * 
 * Shared scroll detection and management for table components.
 * Detects if table content is scrollable and manages scroll element refs.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseTableScrollOptions {
  /**
   * Enable scroll detection
   */
  enabled?: boolean;
  
  /**
   * Debounce delay for resize checks (ms)
   */
  debounceMs?: number;
  
  /**
   * Initial delay before first check (ms)
   */
  initialDelayMs?: number;
  
  /**
   * Optional container selector for wrapper observation
   */
  containerSelector?: string;
}

export interface UseTableScrollReturn {
  /**
   * Ref to attach to the scroll container element
   */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  
  /**
   * The current scroll element (for Scrollbar)
   */
  scrollElement: HTMLDivElement | null;
  
  /**
   * Whether the content is currently scrollable
   */
  isScrollable: boolean;
  
  /**
   * Manually trigger scroll detection
   */
  checkScrollable: () => void;
}

/**
 * Hook for managing table horizontal scrolling
 */
export function useTableScroll(options: UseTableScrollOptions = {}): UseTableScrollReturn {
  const {
    enabled = true,
    debounceMs = 100,
    initialDelayMs = 10,
    containerSelector
  } = options;
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  
  // Update scroll element when ref changes
  useEffect(() => {
    if (enabled && scrollContainerRef.current) {
      setScrollElement(scrollContainerRef.current);
    }
  }, [enabled]);
  
  // Check if content is scrollable
  const checkScrollable = useCallback(() => {
    if (!enabled || !scrollContainerRef.current) {
      setIsScrollable(false);
      return;
    }
    
    const element = scrollContainerRef.current;
    const scrollable = element.scrollWidth > element.clientWidth;
    
    setIsScrollable(scrollable);
  }, [enabled]);
  
  // Monitor scroll element for size changes
  useEffect(() => {
    if (!enabled || !scrollContainerRef.current) {
      return;
    }
    
    let rafId: number | undefined;
    let followUpTimeoutId: NodeJS.Timeout | undefined;
    
    // Initial check using RAF for faster response (runs after next paint)
    rafId = requestAnimationFrame(() => {
      checkScrollable();
      
      // Follow-up check after a short delay to catch any late layout changes
      followUpTimeoutId = setTimeout(checkScrollable, 50);
    });
    
    // Setup ResizeObserver for responsive checks
    const resizeObserver = new ResizeObserver(() => {
      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Debounce the check with reduced delay for faster response
      resizeTimeoutRef.current = setTimeout(checkScrollable, 50);
    });
    
    // Observe the scroll container
    resizeObserver.observe(scrollContainerRef.current);
    
    // Observe any child table elements
    const tableElement = scrollContainerRef.current.querySelector('[role="table"]');
    if (tableElement) {
      resizeObserver.observe(tableElement);
    }
    
    // Observe optional wrapper container
    if (containerSelector) {
      const container = document.querySelector(containerSelector);
      if (container) {
        resizeObserver.observe(container);
      }
    }
    
    // Also check on window resize as fallback
    window.addEventListener('resize', checkScrollable);
    
    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      if (followUpTimeoutId) {
        clearTimeout(followUpTimeoutId);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollable);
    };
  }, [enabled, checkScrollable, debounceMs, initialDelayMs, containerSelector]);
  
  return {
    scrollContainerRef,
    scrollElement,
    isScrollable,
    checkScrollable
  };
}

